<script setup lang="ts">
import type { PaymentMethodsResponseBody } from 'types/vendors/brink'
import { AdyenCheckout, ApplePay, PayPal } from '@adyen/adyen-web'
import { BrinkPaymentErrorCodes } from 'types/vendors/brink'
import '@adyen/adyen-web/styles/adyen.css'
import { hardRedirectAndRetainPath } from '~/lib/storefronts'

interface Props {
  isApplePay: boolean
  amount: { value: number, currency: string }
  paymentMethodsResponse: PaymentMethodsResponseBody
}

const props = withDefaults(defineProps<Props>(), {
})
const emit = defineEmits(['paymentError'])
const expressErrorArray = defineModel<string[]>()
const router = useRouter()
const route = useRoute()
const { $checkoutUrl } = useNuxtApp()
const container = ref<HTMLDivElement>()
const storefrontStore = useStorefrontStore()
const { currentMarketCountryCode, currentMarket } = storeToRefs(storefrontStore)
const expressComponent = ref<PayPal | ApplePay | null>(null)
function isAdyenCheckout(checkout: any): checkout is typeof AdyenCheckout {
  return checkout && typeof checkout !== 'undefined'
}

async function loadExpressFlow() {
  try {
    await startExpressFlow()
  }
  catch (error: any) {
    if (error.code === BrinkPaymentErrorCodes.MISSING_SHIPPING_OPTION_ERROR)
      return router.push($checkoutUrl({ route, view: 'address' }))

    if (error.code === BrinkPaymentErrorCodes.UNAUTHORIZED)
      return router.push($checkoutUrl({ route, view: 'cart' }))

    if (error.code === BrinkPaymentErrorCodes.TAX_JAR_ERROR)
      return router.push($checkoutUrl({ route, view: 'address' }))

    emit('paymentError', error)
  }
}

const ppeEnabledMarkets = ['JP', 'KR']
const disabledPaypalExpress = computed(() => ppeEnabledMarkets.includes(currentMarketCountryCode.value.toUpperCase()))

onMounted(() => {
  if (!props.isApplePay && disabledPaypalExpress.value) {
    // Not enabled 
    return
  }
  loadExpressFlow()
  
})

watch(currentMarketCountryCode, () => {
  expressErrorArray.value = []
  if (disabledPaypalExpress.value && expressComponent.value && !props.isApplePay) {
    expressComponent.value.unmount()
    return
  }
  if (!disabledPaypalExpress.value) 
    hardRedirectAndRetainPath(currentMarket.value)
})

async function startExpressFlow() {
  const expressStore = props.isApplePay ? await useApplePay(props.paymentMethodsResponse) : await usePayPal(props.paymentMethodsResponse)

  const timeout = new Promise((_, reject) =>
    setTimeout(() => reject(new Error('Apple Pay Express loading timed out')), 5000),
  )

  const checkoutComponent = await
  Promise.race([
    expressStore.createAdvancedFlow(expressErrorArray as Ref<string[]>, props.amount),
    timeout,
  ])

  if (!isAdyenCheckout(checkoutComponent))
    throw new TypeError('Invalid adyen checkout')

  expressComponent.value = await expressStore.setUpExpressPayment(checkoutComponent, expressErrorArray as Ref<string[]>)

  if (expressComponent.value && container.value) {
    expressComponent.value
      .isAvailable()

    expressComponent.value.mount(container.value)

    if (!props.isApplePay) {
      expressComponent.value.update({
        style: {
          disableMaxWidth: true,
        },
      })
    }
    const child = container.value.firstElementChild as HTMLElement

    if (child) {
      child.style.setProperty('width', '100%', 'important')
    }
  }
}
</script>

<template>
  <div
    :id="isApplePay ? 'apple-container' : 'paypal-container'"
    ref="container"
    class="express-container"
  />
</template>

<style lang="scss" scoped>
.red {
  color: var(--red);
  margin-bottom: 1rem
}
.express-container {
  width: 100%;
}
#paypal-container {
  display: flex;
  align-items: center;
}
#paypal-container .adyen-checkout__paypal__button{
  margin-bottom: 0px !important
}
@media screen and (min-width:768px) {
  #component-container {
  margin-inline: 0rem;
  }
}
</style>
